
import axios from 'axios'
import { currentAuthSession, ensureAuthSession } from './authSession'

export const API_URL =
  process.env.REACT_APP_API_URL || 'https://sys.chu.kg/api/'

let refreshPromise = null
let refreshFor = null
let refreshSession = null

function clearAuthAndRedirect() {
  try {
    localStorage.removeItem('token')
    localStorage.removeItem('refresh')
    localStorage.removeItem('login_roles')
    localStorage.removeItem('login_primary_role')
    localStorage.removeItem('user')
    localStorage.removeItem('auth_session')
  } catch (_) {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    const path = window.location.pathname || ''
    // Не редиректим, если уже на /login или /recover-*.
    if (
      !path.startsWith('/login') &&
      !path.startsWith('/recover-password') &&
      !path.startsWith('/recover-pin') &&
      !path.startsWith('/account-locked')
    ) {
      window.location.href = '/login'
    }
  }
}

export async function refreshAccessToken() {
  const refresh = (() => {
    try {
      return localStorage.getItem('refresh')
    } catch (_) {
      return null
    }
  })()
  const session = refresh ? ensureAuthSession() : null
  if (refreshPromise && refreshFor === refresh && refreshSession === session) return refreshPromise
  if (!refresh) return Promise.reject(new Error('no_refresh_token'))

  const pending = axios
    .post(
      `${API_URL.replace(/\/$/, '')}/account/token/refresh/`,
      { refresh },
      { headers: { 'Content-Type': 'application/json' } },
    )
    .then((res) => {
      const { access, refresh: newRefresh } = res.data || {}
      if (!access) throw new Error('no_access_in_refresh_response')
      if (currentAuthSession() !== session || localStorage.getItem('refresh') !== refresh) throw new Error('session_changed')
      try {
        localStorage.setItem('token', access)
        if (newRefresh) localStorage.setItem('refresh', newRefresh)
        window.dispatchEvent(new Event('chu:auth-token'))
      } catch (_) {
        /* ignore */
      }
      return access
    })
    .finally(() => {
      if (refreshPromise === pending) { refreshPromise = null; refreshFor = null; refreshSession = null }
    })
  refreshPromise = pending
  refreshFor = refresh
  refreshSession = session
  return refreshPromise
}

/**
 * Подключает 401-перехватчик к axios-инстансу.
 * @param {import('axios').AxiosInstance} instance
 */
export function attachAuthRefresh(instance) {
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('token')
    config._authSession = token ? ensureAuthSession() : null
    if (token) {
      if (!config.headers) config.headers = {}
      config.headers.Authorization = `Bearer ${token}`
    } else if (config.headers) {
      delete config.headers.Authorization
      delete config.headers.authorization
    }
    return config
  })
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status
      const original = error?.config || {}
      // Не пытаемся рефрешить запрос самого refresh'а или login'а.
      const url = (original.url || '').toString()
      const isAuthRoute = /(^|\/)account\/(token|login)(\/|$)/.test(url)
      const header = original.headers?.Authorization || original.headers?.authorization
      const requestToken = typeof header === 'string' && header.startsWith('Bearer ') ? header.slice(7) : null
      // A late response from a previous account must not refresh, retry or
      // redirect the account that has since signed in in this browser.
      const requestSession = original._authSession
      if (!isAuthRoute && (!requestSession || requestSession !== currentAuthSession())) return Promise.reject(error)

      if (status === 401 && !original._retry && !isAuthRoute) {
        original._retry = true
        const refreshAtRequest = localStorage.getItem('refresh')
        try {
          const currentToken = localStorage.getItem('token')
          const newToken = requestToken && currentToken && requestToken !== currentToken ? currentToken : await refreshAccessToken()
          if (currentAuthSession() !== requestSession || localStorage.getItem('token') !== newToken) throw new Error('session_changed')
          if (!original.headers) original.headers = {}
          original.headers.Authorization = `Bearer ${newToken}`
          return instance(original)
        } catch (refreshError) {
          const refreshStatus = refreshError?.response?.status
          if (currentAuthSession() === requestSession && localStorage.getItem('refresh') === refreshAtRequest &&
            ([400, 401, 403].includes(refreshStatus) || refreshError?.message === 'no_refresh_token' || refreshError?.message === 'no_access_in_refresh_response')) {
            clearAuthAndRedirect()
          }
          return Promise.reject(refreshError)
        }
      }

      // 423 / locked → отправляем на страницу блокировки (без чистки токена).
      if (status === 423 && typeof window !== 'undefined') {
        const path = window.location.pathname || ''
        if (!path.startsWith('/account-locked')) {
          window.location.href = '/account-locked/'
        }
      }

      return Promise.reject(error)
    },
  )
}

export default attachAuthRefresh
